import * as d3 from 'd3';
import { versionColor } from '../colors.js';

// Inventory: three rows (Assets, Dictionaries, Deltas). Each item = colored rect.
// Layout per row:
//   [label band, LABEL_H px]
//   [items grid, wraps within rowBody]
// If items exceed the grid capacity, they're truncated with a "+N more" tag.
export function createInventoryChart(svgEl) {
  const svg = d3.select(svgEl);
  const groupsG = svg.append('g');
  function update(snap) {
    const bounds = svgEl.getBoundingClientRect();
    const W = bounds.width, H = bounds.height;
    svg.attr('viewBox', `0 0 ${W} ${H}`);
    const rows = [
      { label: 'Assets', items: snap.versions.map((v) => ({ kind: 'asset', assetId: v.assetId, v: v.version, key: v.key })) },
      { label: 'Dictionaries', items: snap.dictionaries.map((d) => ({ kind: 'dict', assetId: d.sourceAssetId, v: d.sourceVersion, key: d.id, pinned: d.pinned })) },
      { label: 'Deltas', items: snap.deltas.map((d) => ({ kind: 'delta', assetId: d.assetId, fromV: d.fromVersion, toV: d.toVersion, key: d.id })) },
    ];
    const MARGIN_TOP = 4;
    const MARGIN_BOTTOM = 4;
    const ROW_GAP = 6;
    const LABEL_H = 14;   // reserved for the row title
    const box = 12;
    const gap = 3;
    const rowH = (H - MARGIN_TOP - MARGIN_BOTTOM - ROW_GAP * (rows.length - 1)) / rows.length;
    const bodyH = Math.max(box, rowH - LABEL_H);
    const perRow = Math.max(1, Math.floor((W - 16) / (box + gap)));
    const linesPerRow = Math.max(1, Math.floor((bodyH + gap) / (box + gap)));
    const capacity = perRow * linesPerRow;

    groupsG.selectAll('g.row').data(rows, (r) => r.label).join(
      (enter) => {
        const g = enter.append('g').attr('class', 'row');
        g.append('text').attr('class', 'lbl').attr('x', 8).attr('font-size', 10).attr('fill', '#8b949e');
        g.append('text').attr('class', 'overflow').attr('font-size', 10).attr('fill', '#6e7681').attr('text-anchor', 'end');
        g.append('g').attr('class', 'items');
        return g;
      }
    ).attr('transform', (_, i) => `translate(0, ${MARGIN_TOP + i * (rowH + ROW_GAP)})`).each(function (row) {
      const g = d3.select(this);
      g.select('text.lbl').attr('y', 10).text(`${row.label} (${row.items.length})`);
      g.select('text.overflow')
        .attr('x', W - 8).attr('y', 10)
        .text(row.items.length > capacity ? `+${row.items.length - capacity} more` : '');

      const visible = row.items.slice(0, capacity);
      const items = g.select('g.items').attr('transform', `translate(0, ${LABEL_H})`);
      const sel = items.selectAll('g.item').data(visible, (d) => d.key);
      const enter = sel.enter().append('g').attr('class', 'item');
      enter.append('rect').attr('width', box).attr('height', box).attr('rx', 2);
      sel.merge(enter)
        .attr('transform', (_, i) => `translate(${8 + (i % perRow) * (box + gap)}, ${Math.floor(i / perRow) * (box + gap)})`)
        .select('rect')
        .attr('fill', (d) => d.kind === 'delta' ? 'url(#none)' : versionColor(d.assetId, d.v))
        .attr('stroke', (d) => d.kind === 'dict' ? '#f0b429' : 'none')
        .attr('stroke-width', (d) => d.kind === 'dict' ? 2 : 0)
        .attr('stroke-dasharray', null)
        .style('filter', (d) => d.pinned ? 'drop-shadow(0 0 3px #f0b429)' : null);

      // Delta gradients (defs) — keyed on visible items only.
      const defs = svg.selectAll('defs').data([0]).join('defs');
      const grads = defs.selectAll('linearGradient.delta').data(visible.filter((d) => d.kind === 'delta'), (d) => d.key);
      const gEnter = grads.enter().append('linearGradient').attr('class', 'delta').attr('id', (d) => 'g_' + d.key)
        .attr('x1', 0).attr('x2', 1).attr('y1', 0).attr('y2', 0);
      gEnter.append('stop').attr('offset', '0%');
      gEnter.append('stop').attr('offset', '100%');
      const merged = grads.merge(gEnter);
      merged.select('stop:nth-child(1)').attr('stop-color', (d) => versionColor(d.assetId, d.fromV));
      merged.select('stop:nth-child(2)').attr('stop-color', (d) => versionColor(d.assetId, d.toV));
      grads.exit().remove();

      sel.merge(enter).select('rect').filter((d) => d.kind === 'delta').attr('fill', (d) => `url(#g_${d.key})`);
      sel.exit().remove();
    });
  }
  return { update };
}
